import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from 'src/components/atoms/Loader';
import Text, { TextVariant } from 'src/components/atoms/Text';
import { ShowState } from 'src/components/molecules/ShowState';
import { LinkPropertyOwnerModal } from 'src/components/organisms/LinkPropertyOwnerModal';
import { OwnerForm } from 'src/components/organisms/OwnerForm';
import { OwnerPropertiesTable } from 'src/components/organisms/OwnerPropertiesTable';
import { DetailView } from 'src/components/templates';
import useOwner from 'src/features/owner/hooks/useOwner';
import { IOwner } from 'src/features/owner/ts/interfaces/Owner';
import { OwnerEditInfoContainer, PropertiesLink } from './styles';

const OwnerEditPage: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();
  const { owner, isLoading, isUpdating, onUpdate, onReset } = useOwner(ownerId);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!ownerId) {
    return (
      <ShowState type="information" variant="error" message="No Owner ID provided in the URL." />
    );
  }

  if (isLoading || isUpdating) {
    return <Loader />;
  }

  if (!owner) {
    return (
      <ShowState
        type="information"
        variant="error"
        message="There was an error fetching the owner."
        buttonLabel="Reload"
        onClick={() => window.location.reload()}
      />
    );
  }

  const onSave = (updatedOwner: Partial<IOwner>) => {
    // Implement the save logic here, e.g., call an update function from useOwner
    onUpdate(ownerId, { owner: updatedOwner });
  };

  return (
    <DetailView back={{ to: '/owners', label: 'Go Back' }} title="Owner Details">
      <OwnerForm defaultValues={owner} onSave={onSave} />
      <OwnerEditInfoContainer>
        <Text variant={TextVariant.normal}>
          Properties Linked:{' '}
          <Text isBold data-testid="owner-linked-properties-count">
            {owner.linkedPropertiesCount || 0}
          </Text>
        </Text>

        <Text variant={TextVariant.small}>
          <PropertiesLink onClick={() => setIsModalOpen(true)}>Link Properties</PropertiesLink>
        </Text>
      </OwnerEditInfoContainer>
      <OwnerPropertiesTable ownerId={ownerId} />
      <LinkPropertyOwnerModal
        ownerId={ownerId}
        isOpen={isModalOpen}
        closeModal={() => {
          onReset();
          setIsModalOpen(false);
        }}
      />
    </DetailView>
  );
};

export default OwnerEditPage;
